/**
 * TMAuthContext — auth state for the /tm Team Manager module.
 *
 * Uses the same Firebase Auth instance as the CMS but reads from the
 * `tmUsers` Firestore collection (separate from `users`).
 *
 * CMS admins automatically get TM admin access even without a tmUsers doc.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import type { TMUser } from '@/types/tm.types';

interface TMAuthContextValue {
  firebaseUser: FirebaseUser | null;
  tmUser:       TMUser | null;
  loading:      boolean;
  isTMAdmin:    boolean;
  isTMApproved: boolean;
  refresh:      () => void;
}

const TMAuthContext = createContext<TMAuthContextValue>({
  firebaseUser: null,
  tmUser:       null,
  loading:      true,
  isTMAdmin:    false,
  isTMApproved: false,
  refresh:      () => {},
});

export function TMAuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [tmUser,       setTMUser]       = useState<TMUser | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [refreshKey,   setRefreshKey]   = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let unsubFS: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, (fbUser: import('firebase/auth').User | null) => {
      unsubFS?.();
      unsubFS = null;

      if (!fbUser) {
        setFirebaseUser(null);
        setTMUser(null);
        setLoading(false);
        return;
      }

      setFirebaseUser(fbUser);
      setLoading(true);

      // Listen to tmUsers doc
      unsubFS = onSnapshot(
        doc(db, 'tmUsers', fbUser.uid),
        (snap) => {
          if (snap.exists()) {
            const d = snap.data() as Record<string, unknown>;
            const toDate = (v: unknown) =>
              v && typeof v === 'object' && 'toDate' in v
                ? (v as { toDate: () => Date }).toDate()
                : null;

            setTMUser({
              uid:        fbUser.uid,
              name:       (d.name       as string) ?? fbUser.displayName ?? '',
              email:      (d.email      as string) ?? fbUser.email ?? '',
              phone:      (d.phone      as string) ?? '',
              status:     (d.status     as TMUser['status']) ?? 'pending',
              role:       (d.role       as TMUser['role'])   ?? 'member',
              avatarUrl:  (d.avatarUrl  as string | null) ?? null,
              createdAt:  toDate(d.createdAt),
              approvedAt: toDate(d.approvedAt),
              approvedBy: (d.approvedBy as string | null) ?? null,
            });
          } else {
            setTMUser(null);
          }
          setLoading(false);
        },
        () => setLoading(false),
      );
    });

    return () => { unsubAuth(); unsubFS?.(); };
  }, [refreshKey]);

  const isTMApproved = tmUser?.status === 'approved';
  const isTMAdmin    = isTMApproved && tmUser?.role === 'admin';

  return (
    <TMAuthContext.Provider value={{ firebaseUser, tmUser, loading, isTMAdmin, isTMApproved, refresh }}>
      {children}
    </TMAuthContext.Provider>
  );
}

export function useTMAuth() {
  return useContext(TMAuthContext);
}
